import { useEffect, useState } from 'react'

import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { formatUnits, parseUnits } from 'viem'
import { useAccount, useSignMessage } from 'wagmi'

import { buildRedemptionMessage } from '@/lib/redemptionRequest'

interface RequestRedemptionProps {
  availableLiquidity?: bigint
  defaultAmount?: string
  token?: string
  // When true the trigger is styled as the primary next step (used when an
  // instant on-chain withdrawal is blocked by insufficient pool liquidity).
  emphasize?: boolean
  triggerLabel?: string
}

const fieldLabelStyle = {
  color: 'var(--secondary)',
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 8,
}
const optionalStyle = { color: 'var(--faint)', fontWeight: 400 }
const modalContentStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--rule-light)',
  borderRadius: '16px',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  color: 'var(--ink)',
}
const TOKEN_DECIMALS = 18
const DECIMAL_AMOUNT_RE = /^(?:\d+(?:\.\d{0,18})?|\.\d{1,18})$/

function parseAmount(value: string): bigint | null {
  const normalized = value.trim()
  if (!DECIMAL_AMOUNT_RE.test(normalized)) return null

  try {
    const parsed = parseUnits(normalized, TOKEN_DECIMALS)
    return parsed > 0n ? parsed : null
  } catch {
    return null
  }
}

function formatLiquidity(value: bigint): string {
  const [whole, fraction = ''] = formatUnits(value, TOKEN_DECIMALS).split('.')
  const shortFraction = fraction.slice(0, 4).replace(/0+$/, '')
  const groupedWhole = BigInt(whole).toLocaleString('en-US')
  return shortFraction ? `${groupedWhole}.${shortFraction}` : groupedWhole
}

export default function RequestRedemption({
  availableLiquidity,
  defaultAmount = '',
  token = 'AVAX',
  emphasize = false,
  triggerLabel = 'Request redemption',
}: RequestRedemptionProps) {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { address, chain } = useAccount()
  const { signMessageAsync } = useSignMessage()
  const toast = useToast()

  const [amount, setAmount] = useState(defaultAmount)
  const [message, setMessage] = useState('')
  const [contact, setContact] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [touched, setTouched] = useState(false)

  // Seed the amount from the withdraw input each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setAmount(defaultAmount)
      setMessage('')
      setContact('')
      setSubmitted(false)
      setTouched(false)
    }
  }, [isOpen, defaultAmount])

  const amountWei = parseAmount(amount)
  const amountInvalid = touched && amountWei === null
  const canWithdrawNow =
    amountWei !== null && availableLiquidity !== undefined && amountWei <= availableLiquidity

  const submit = async () => {
    setTouched(true)
    if (amountWei === null || canWithdrawNow || !address) return

    setSubmitting(true)
    try {
      const chainId = chain?.id || 43114
      const vault = token.toUpperCase()

      // Get a one-time nonce. The server sets the issue and expiry times.
      const nonceRes = await fetch('/api/request-redemption', { cache: 'no-store' })
      const nonceData = await nonceRes.json().catch(() => ({}))
      if (!nonceRes.ok || !nonceData?.nonce) {
        throw new Error(nonceData?.error || 'Could not start the request. Please try again.')
      }

      let signature: string
      try {
        signature = await signMessageAsync({
          account: address,
          message: buildRedemptionMessage({
            address,
            amount: formatUnits(amountWei, TOKEN_DECIMALS),
            chainId,
            expirationTime: new Date(nonceData.expirationTime),
            host: window.location.host,
            issuedAt: new Date(nonceData.issuedAt),
            nonce: nonceData.nonce,
            vault,
          }),
        })
      } catch {
        throw new Error('Sign the message in your wallet to send the request.')
      }

      const res = await fetch('/api/request-redemption', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          amountWei: amountWei.toString(),
          chainId,
          message,
          contact,
          walletAddress: address,
          vault,
          nonce: nonceData.nonce,
          signature,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (data?.code === 'LIQUIDITY_AVAILABLE') {
        toast({
          position: 'top',
          title: 'Liquidity is available',
          description: data.error,
          status: 'info',
          duration: 12000,
          isClosable: true,
        })
        return
      }
      if (!res.ok) throw new Error(data?.error || 'Something went wrong. Please try again.')
      setSubmitted(true)
    } catch (err) {
      toast({
        position: 'top',
        title: 'Could not send request',
        description:
          err instanceof Error ? err.message : 'Please try again or email contact@seafi.app.',
        status: 'error',
        duration: 12000,
        isClosable: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <button
        className={`rng-btn ${emphasize ? 'rng-btn--ink' : 'rng-btn--ghost'}`}
        onClick={onOpen}
        style={{ width: '100%' }}
        type="button"
      >
        {triggerLabel}
      </button>

      <Modal isCentered isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="lg">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          maxH="calc(100dvh - 48px)"
          mx="4"
          my="6"
          p={{ base: '5', sm: '6' }}
          style={modalContentStyle}
        >
          <ModalCloseButton
            _focus={{ boxShadow: 'none' }}
            _focusVisible={{ boxShadow: '0 0 0 2px var(--accent)', outline: 'none' }}
            _hover={{ background: 'var(--surface-quiet)', color: 'var(--ink)' }}
            borderRadius="12px"
            color="var(--muted)"
          />

          {submitted ? (
            <>
              <ModalHeader fontSize="24px" fontWeight="bold" pb="2" pt="0" px="0">
                Request received
              </ModalHeader>
              <ModalBody px="0">
                <p style={{ color: 'var(--body)' }}>
                  Thanks. We have your request for{' '}
                  <b>
                    {amount} {token}
                  </b>{' '}
                  and will re-add liquidity within <b>24 hours</b>. Check back on the withdraw form
                  to complete your redemption. You can close this window.
                </p>
              </ModalBody>
              <ModalFooter gap="3" pb="0" px="0">
                <button className="rng-btn rng-btn--ink" onClick={onClose} type="button">
                  Done
                </button>
              </ModalFooter>
            </>
          ) : (
            <>
              <ModalHeader fontSize="24px" fontWeight="bold" pb="2" pt="0" px="0">
                Request a redemption
              </ModalHeader>
              <ModalBody px="0">
                <div className="flex flex-col gap-4">
                  <p style={{ color: 'var(--body)', fontSize: 16 }}>
                    Tell us how much {token} you want to redeem. We re-add liquidity for redemption
                    requests within <b>24 hours</b>, so check back on the withdraw form to complete
                    it. This sends a request only; it does not move any funds yet.
                  </p>

                  <div>
                    <div style={fieldLabelStyle}>Amount</div>
                    <div
                      className="rng-field flex items-center gap-2"
                      style={amountInvalid ? { borderColor: 'var(--crit)' } : undefined}
                    >
                      <input
                        className="rng-input min-w-0 flex-1"
                        inputMode="decimal"
                        onBlur={() => setTouched(true)}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0.00"
                        style={{ fontSize: 16 }}
                        value={amount}
                      />
                      <span style={{ color: 'var(--muted)', fontWeight: 500 }}>{token}</span>
                    </div>
                    {amountInvalid && (
                      <p style={{ color: 'var(--crit)', fontSize: 12, marginTop: 8 }}>
                        Enter an amount greater than zero, with up to 18 decimal places.
                      </p>
                    )}
                  </div>

                  {canWithdrawNow && availableLiquidity !== undefined && (
                    <div
                      role="status"
                      style={{
                        background: 'var(--warn-wash)',
                        border: '1px solid var(--rule-light)',
                        borderRadius: 'var(--radius-control)',
                        padding: 16,
                      }}
                    >
                      <p style={{ color: 'var(--body)', fontSize: 14, lineHeight: 1.55 }}>
                        <b>The vault already has enough liquidity for this amount.</b> Up to{' '}
                        {formatLiquidity(availableLiquidity)} {token} is available now. Close this
                        request and use the withdrawal form instead. Your wallet balance still
                        applies.
                      </p>
                    </div>
                  )}

                  <div>
                    <div style={fieldLabelStyle}>
                      Message <span style={optionalStyle}>(optional)</span>
                    </div>
                    <div className="rng-field">
                      <textarea
                        className="rng-input"
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Anything the team should know about your request"
                        rows={3}
                        style={{ fontSize: 16, resize: 'vertical' }}
                        value={message}
                      />
                    </div>
                  </div>

                  <div>
                    <div style={fieldLabelStyle}>
                      Contact <span style={optionalStyle}>(optional)</span>
                    </div>
                    <div className="rng-field flex items-center">
                      <input
                        className="rng-input"
                        onChange={(e) => setContact(e.target.value)}
                        placeholder="Email, Telegram, or Discord"
                        style={{ fontSize: 16 }}
                        value={contact}
                      />
                    </div>
                  </div>

                  {address ? (
                    <p style={{ color: 'var(--muted)', fontSize: 13 }}>
                      Your wallet {address.slice(0, 6)}…{address.slice(-4)} will ask you to sign a
                      message from SeaFi. Signing proves you hold the deposit. It is not a
                      transaction: it does not move funds, approve tokens, or cost gas.
                    </p>
                  ) : (
                    <p style={{ color: 'var(--crit)', fontSize: 13 }}>
                      Connect the wallet that holds your vault deposit to send a request.
                    </p>
                  )}
                </div>
              </ModalBody>
              <ModalFooter gap="3" pb="0" px="0">
                <button
                  className="rng-btn rng-btn--ink"
                  disabled={submitting || canWithdrawNow || !address}
                  onClick={submit}
                  type="button"
                >
                  {submitting ? 'Check your wallet…' : 'Sign and send request'}
                </button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
